import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Shield, Smartphone, Key, Lock, CheckCircle, Copy, AlertTriangle, QrCode
} from 'lucide-react';
import axios from 'axios';

const TwoFactorSetup = () => {
  const [status, setStatus] = useState({ enabled: false, global_enabled: false, has_secret: false });
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('status'); // status, setup, verify
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/2fa/status`,
        { headers: getAuthHeader() }
      );
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching 2FA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/api/2fa/setup`,
        {},
        { headers: getAuthHeader() }
      );
      setSetupData(response.data);
      setStep('setup');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      alert('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${API_URL}/api/2fa/verify-setup`,
        { code: verifyCode },
        { headers: getAuthHeader() }
      );
      alert('2FA enabled successfully!');
      setStep('status');
      setSetupData(null);
      setVerifyCode('');
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode) {
      alert('Please enter your verification code');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${API_URL}/api/2fa/disable`,
        { code: disableCode },
        { headers: getAuthHeader() }
      );
      alert('2FA disabled successfully');
      setDisableCode('');
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading && step === 'status') {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <p className="text-center py-8 text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Shield className="h-6 w-6" />
        Two-Factor Authentication
      </h1>

      {/* Global Status Check */}
      {!status.global_enabled && (
        <Card className="bg-gray-50 border-gray-200 mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Lock className="h-8 w-8 text-gray-400" />
              <div>
                <h3 className="font-semibold text-gray-700">2FA Not Available</h3>
                <p className="text-sm text-gray-500">
                  Two-factor authentication is currently disabled by the administrator.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {status.global_enabled && step === 'status' && (
        <>
          {/* Current Status */}
          <Card className={status.enabled ? 'bg-green-50 border-green-200 mb-6' : 'bg-blue-50 border-blue-200 mb-6'}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                {status.enabled ? (
                  <>
                    <CheckCircle className="h-10 w-10 text-green-500" />
                    <div>
                      <h3 className="font-semibold text-green-800">2FA Enabled</h3>
                      <p className="text-sm text-green-600">
                        Your account is protected with two-factor authentication.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Smartphone className="h-10 w-10 text-blue-500" />
                    <div>
                      <h3 className="font-semibold text-blue-800">Enable 2FA</h3>
                      <p className="text-sm text-blue-600">
                        Add an extra layer of security to your account.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Enable Section */}
          {!status.enabled && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Set Up 2FA</CardTitle>
                <CardDescription>
                  Use an authenticator app like Google Authenticator or Authy
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={startSetup} disabled={loading} className="w-full">
                  <Smartphone className="h-4 w-4 mr-2" />
                  {loading ? 'Loading...' : 'Start Setup'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Disable Section */}
          {status.enabled && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Disable 2FA
                </CardTitle>
                <CardDescription>
                  Enter your authenticator code or a backup code to disable 2FA
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Verification Code</Label>
                  <Input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="Enter 6-digit code or backup code"
                    maxLength={8}
                  />
                </div>
                <Button
                  variant="destructive"
                  onClick={disable2FA}
                  disabled={loading || !disableCode}
                  className="w-full"
                >
                  {loading ? 'Processing...' : 'Disable 2FA'}
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Setup Flow */}
      {status.global_enabled && step === 'setup' && setupData && (
        <Card>
          <CardHeader>
            <CardTitle>Scan QR Code</CardTitle>
            <CardDescription>
              Scan this QR code with your authenticator app
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* QR Code */}
            <div className="flex justify-center">
              <img
                src={setupData.qr_code}
                alt="2FA QR Code"
                className="border rounded-lg p-4 bg-white"
              />
            </div>

            {/* Manual Entry */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-2">Can't scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                  {setupData.secret}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(setupData.secret)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Backup Codes */}
            <div>
              <Button
                variant="outline"
                onClick={() => setShowBackupCodes(!showBackupCodes)}
                className="w-full"
              >
                <Key className="h-4 w-4 mr-2" />
                {showBackupCodes ? 'Hide Backup Codes' : 'Show Backup Codes'}
              </Button>
              {showBackupCodes && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 mb-3">
                    <AlertTriangle className="h-4 w-4 inline mr-1" />
                    Save these backup codes in a safe place. You can use them if you lose access to your authenticator.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {setupData.backup_codes.map((code, index) => (
                      <code key={index} className="bg-white px-3 py-1 rounded text-center font-mono">
                        {code}
                      </code>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full"
                    onClick={() => copyToClipboard(setupData.backup_codes.join('\n'))}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy All Codes
                  </Button>
                </div>
              )}
            </div>

            {/* Verify Code */}
            <div className="border-t pt-6">
              <Label>Enter Verification Code</Label>
              <p className="text-sm text-gray-500 mb-3">
                Enter the 6-digit code from your authenticator app
              </p>
              <Input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="text-center text-2xl tracking-widest font-mono"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('status');
                  setSetupData(null);
                  setVerifyCode('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={verifySetup}
                disabled={loading || verifyCode.length !== 6}
                className="flex-1"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">1</span>
              <span>Download an authenticator app (Google Authenticator, Authy, etc.)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">2</span>
              <span>Scan the QR code or enter the secret key manually</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">3</span>
              <span>Enter the 6-digit code from your app to verify</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">4</span>
              <span>Save your backup codes in a secure location</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFactorSetup;
